import { useEffect, useState } from 'react';
import { rbacService, rolesApi } from '@services/api.ts';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Container,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Checkbox,
  FormControlLabel,
  Button,
} from '@mui/material';
import { ExpandMore, VpnKey } from '@mui/icons-material';
import type { RolePermission, RoleId, RolesResponse } from '@/types/roles';

const groupPermissionsByFeature = (permissions: RolePermission[]) => {
  return permissions.reduce(
    (acc, permission) => {
      const feature = permission.feature;
      if (!acc[feature]) {
        acc[feature] = [];
      }
      acc[feature].push(permission);
      return acc;
    },
    {} as Record<string, RolePermission[]>
  );
};

export const RolesPermissions = () => {
  const [roles, setRoles] = useState<RolesResponse[]>([]);
  const [selectedRole, setSelectedRole] = useState<RoleId | null>(null);
  const [isLoadingRoles, setIsLoadingRoles] = useState<boolean>(false);
  const [isLoadingPermissions, setIsLoadingPermissions] =
    useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<RolePermission[]>([]);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<
    Set<number>
  >(new Set());
  const [originalPermissionIds, setOriginalPermissionIds] = useState<
    Set<number>
  >(new Set());

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setIsLoadingRoles(true);
        setError(null);
        const rolesData = await rolesApi.getRoles();
        setRoles(rolesData.results);
      } catch (error: any) {
        setError(error.message);
      } finally {
        setIsLoadingRoles(false);
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    if (!selectedRole) return;

    const fetchPermissions = async () => {
      try {
        setIsLoadingPermissions(true);
        setError(null);
        setSaveSuccess(false);

        const data = await rbacService.getRolePermissions(selectedRole);
        setPermissions(data);

        const assignedIds = new Set(
          data.filter((p) => p.is_assigned).map((p) => p.id)
        );
        setSelectedPermissionIds(assignedIds);
        setOriginalPermissionIds(assignedIds);
      } catch (error: any) {
        setError(error.message);
      } finally {
        setIsLoadingPermissions(false);
      }
    };

    fetchPermissions();
  }, [selectedRole]);

  const handleRoleClick = (roleId: RoleId) => {
    setSelectedRole(roleId);
    setSaveSuccess(false);
    setError(null);
  };

  const handlePermissionToggle = (permissionId: number) => {
    setSelectedPermissionIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(permissionId)) {
        newSet.delete(permissionId);
      } else {
        newSet.add(permissionId);
      }
      return newSet;
    });
    setSaveSuccess(false);
    setError(null);
  };

  const hasChanges = () => {
    if (selectedPermissionIds.size !== originalPermissionIds.size) {
      return true;
    }

    for (const id of selectedPermissionIds) {
      if (!originalPermissionIds.has(id)) {
        return true;
      }
    }

    return false;
  };

  const handleSave = async () => {
    if (!selectedRole) return;

    try {
      setIsSaving(true);
      setError(null);
      setSaveSuccess(false);
      const permissionIds = Array.from(selectedPermissionIds);

      await rbacService.updateRolePermissions(selectedRole, permissionIds);

      setOriginalPermissionIds(new Set(permissionIds));
      setSaveSuccess(true);
    } catch (error: any) {
      setError(`Failed to save: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedPermissionIds(new Set(originalPermissionIds));
    setSaveSuccess(false);
    setError(null);
  };

  const groupedPermissions = groupPermissionsByFeature(permissions);
  const selectedRoleData = roles.find((r) => r.id === selectedRole);
  const showChangesIndicator = hasChanges();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box mb={4}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Roles & Permissions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a role to view and manage permissions
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            {isLoadingRoles ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              roles.map((role) => (
                <Card
                  key={role.id}
                  onClick={() => handleRoleClick(role.id)}
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    borderWidth: 2,
                    borderColor:
                      selectedRole === role.id ? 'primary.main' : 'divider',
                    bgcolor:
                      selectedRole === role.id
                        ? 'action.selected'
                        : 'transparent',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {role.display_name}
                    </Typography>
                  </CardContent>
                </Card>
              ))
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={8}>
          {!selectedRole ? (
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight={300}
                >
                  <VpnKey
                    sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }}
                  />
                  <Typography color="text.secondary">
                    Select a role to view permissions
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card variant="outlined">
              <CardContent>
                {selectedRoleData && (
                  <Box mb={3}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={2}
                    >
                      <Typography variant="h6" fontWeight={600}>
                        {selectedRoleData.display_name}
                      </Typography>

                      {showChangesIndicator && (
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleCancel}
                            disabled={isSaving}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleSave}
                            disabled={isSaving}
                          >
                            {isSaving ? 'Saving...' : 'Save'}
                          </Button>
                        </Stack>
                      )}
                    </Box>

                    {showChangesIndicator && (
                      <Typography
                        variant="body2"
                        color="info.main"
                        sx={{ fontWeight: 500 }}
                      >
                        You have unsaved changes
                      </Typography>
                    )}

                    {saveSuccess && (
                      <Typography
                        variant="body2"
                        color="success.main"
                        sx={{ fontWeight: 500, mt: 1 }}
                      >
                        Permissions updated successfully
                      </Typography>
                    )}

                    {error && (
                      <Typography
                        variant="body2"
                        color="error.main"
                        sx={{ fontWeight: 500, mt: 1 }}
                      >
                        {error}
                      </Typography>
                    )}
                  </Box>
                )}

                {isLoadingPermissions ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress size={32} />
                  </Box>
                ) : permissions.length === 0 ? (
                  <Typography color="text.secondary" align="center" py={4}>
                    No permissions available
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {Object.entries(groupedPermissions).map(
                      ([feature, perms]) => (
                        <Accordion
                          key={feature}
                          disableGutters
                          elevation={0}
                          sx={{
                            '&:before': {
                              display: 'none',
                            },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{
                              px: 2,
                              minHeight: 48,
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={600}>
                              {feature.replace(/_/g, ' ').toUpperCase()}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              (
                              {
                                perms.filter((p) =>
                                  selectedPermissionIds.has(p.id)
                                ).length
                              }
                              /{perms.length})
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                            <Stack spacing={0.5}>
                              {perms.map((permission) => (
                                <FormControlLabel
                                  key={permission.id}
                                  control={
                                    <Checkbox
                                      checked={selectedPermissionIds.has(
                                        permission.id
                                      )}
                                      onChange={() =>
                                        handlePermissionToggle(permission.id)
                                      }
                                      size="small"
                                      disabled={isSaving}
                                    />
                                  }
                                  label={
                                    <Box>
                                      <Typography variant="body2">
                                        {permission.action}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              ))}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      )
                    )}
                  </Stack>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};
